import { useEffect, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  BasicDatePicker,
  ContentCard,
  MasterStackBarChart,
  MetricCard,
  ScalrCarousel,
} from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import BonusPayoutChart, {
  type KpiRealizationItem,
} from '@/components/ui/charts/BonusPayoutChart'
import { chartColors, metricCardColors } from '@/constants/COLOR_PALETTE'
import {
  toast,
  useDashboardBranchPerformance,
  useDashboardMetricCards,
} from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { addAlert, useAppDispatch, useReduxBaseFilter } from '@/store'
import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'
import { DateType } from '@/utils/date'
import {
  IconBox,
  IconBrandSpeedtest,
  IconBuildingStore,
  IconCashBanknote,
  IconChartArrowsVertical,
  IconChartBar,
  IconCreditCard,
  IconCurrencyDollar,
  IconPercentage,
  IconReceipt,
} from '@tabler/icons-react'

const BONUS_PAYOUT_MOCK = {
  value: 3700,
  max: 4000,
  unit: '$',
  kpiRealizations: [
    { title: 'ADNS', value: 2000, maxValue: 2000 },
    { title: 'COGS', value: 1000, maxValue: 1000 },
    { title: 'Labor', value: 700, maxValue: 1000 },
    { title: 'OpEx', value: 500, maxValue: 1000 },
    { title: 'Rent', value: 300, maxValue: 1000 },
  ] as KpiRealizationItem[],
}

const MINI_METRIC_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[1] }}
      />
    ),
  },
  {
    key: 'storeProfits' as const,
    header: t('clarity.pnl.storeProfits'),
    icon: (
      <IconCreditCard className="w-6 h-6" style={{ color: chartColors[0] }} />
    ),
  },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

const CHART_DATA_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

function DashboardContent() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('dashboard')

  // Chart-specific date filter (independent of Redux dashboard date)
  const [chartDate, setChartDate] = useState<DateRange | undefined>(date)
  const [chartDateFilterType, setChartDateFilterType] =
    useState<DateType>(dateFilterType)

  // Sync chart date from Redux once when dashboard date becomes available (initial load only)
  const hasSyncedChartDate = useRef(false)
  useEffect(() => {
    if (!hasSyncedChartDate.current && date?.from != null) {
      setChartDate(date)
      setChartDateFilterType(dateFilterType)
      hasSyncedChartDate.current = true
    }
  }, [date, dateFilterType])

  const {
    metricCards,
    loading: metricCardsLoading,
    error: metricCardsError,
    refetch: metricCardsRefetch,
  } = useDashboardMetricCards(
    dateToString(date, 'from', 'yyyy-mm-dd'),
    dateToString(date, 'to', 'yyyy-mm-dd'),
    selectedBranch,
    dateFilterType === 'test-range'
      ? 'month-to-date'
      : (dateFilterType as DateType)
  )

  const {
    branchPerformanceChartData,
    systemTotalPerformance,
    metricCardRecord,
    miniCardData,
    trendData,
    loading: branchPerformanceLoading,
    error: branchPerformanceError,
    refetch: branchPerformanceRefetch,
  } = useDashboardBranchPerformance(selectedBranch, chartDate)

  useEffect(() => {
    if (metricCardsError) {
      toast({
        title: t('common.errors.serverError'),
        description: metricCardsError.message,
        variant: 'destructive',
      })
    }
  }, [metricCardsError])

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle pageTitle>{t('clarity.dashboard')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="dashboard" />

        <div className="space-y-6">
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              {/* TODO: remove isNewLogic after finalize migration fe logic to backend */}
              <MetricCard
                title={t('clarity.metrics.adns')}
                subTitle={t('clarity.metrics.adnsSubtitle')}
                mtdCurrContent={metricCards?.adns.mom}
                ytdCurrContent={metricCards?.adns.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.adns.value}
                type="money"
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.red500}
                loading={metricCardsLoading}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.cogs')}
                subTitle={t('clarity.metrics.cogsSubtitle')}
                mtdCurrContent={metricCards?.cogs?.mom}
                ytdCurrContent={metricCards?.cogs?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.cogs?.value}
                type="money"
                icon={<IconCashBanknote color="white" size={20} />}
                color={metricCardColors.red500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.laborRatio')}
                subTitle={t('clarity.metrics.laborRatioSubtitle')}
                mtdCurrContent={metricCards?.labor?.mom}
                ytdCurrContent={metricCards?.labor?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.labor?.value}
                type="percentage"
                icon={<IconPercentage color="white" size={20} />}
                color={metricCardColors.green500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                isNewLogic
              />
              <MetricCard
                title={t('clarity.metrics.speedOfService')}
                subTitle={t('clarity.metrics.speedOfServiceSubtitle')}
                mtdCurrContent={metricCards?.speed?.mom}
                ytdCurrContent={metricCards?.speed?.yoy}
                mtdPrevContent={0}
                ytdPrevContent={0}
                content={metricCards?.speed?.value}
                type="seconds"
                icon={<IconBrandSpeedtest color="white" size={20} />}
                color={metricCardColors.orange500}
                loading={metricCardsLoading}
                isIncreasingPositive={false}
                isNewLogic
              />
            </ScalrCarousel>
          </div>

          <MasterStackBarChart
            title={t('dashboard.performanceByDivision')}
            metricCardRecord={metricCardRecord}
            miniCardData={miniCardData}
            trendData={trendData}
            loading={branchPerformanceLoading}
            miniMetricConfigs={MINI_METRIC_CONFIGS(t)}
            chartDataConfigs={CHART_DATA_CONFIGS(t)}
            customComponent={
              <BasicDatePicker
                value={chartDate}
                dateFilterType={chartDateFilterType}
                onChange={(newDate, newDateFilterType) => {
                  setChartDate(newDate)
                  setChartDateFilterType(newDateFilterType)
                }}
              />
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-1">
              <ContentCard
                title={t('dashboard.bonusPayoutProgress')}
                loading={false}
                onViewMore={() => {
                  navigate('/targets/kpi-payout')
                }}
                isBeta
              >
                <BonusPayoutChart
                  value={BONUS_PAYOUT_MOCK.value}
                  max={BONUS_PAYOUT_MOCK.max}
                  unit={BONUS_PAYOUT_MOCK.unit}
                  kpiRealizations={BONUS_PAYOUT_MOCK.kpiRealizations}
                />
              </ContentCard>
            </div>
            <div className="col-span-2">
              <ContentCard
                title={t('dashboard.scalrPulse')}
                loading={false}
                isBeta
              >
                Scalr Pulse
              </ContentCard>
            </div>
          </div>

          {/* <div>
            <DataTable
              columns={groupedCockpitMetricsColumns}
              data={[companyData, ...branchData].filter(
                (item): item is CockpitMetricsModel => item !== undefined
              )}
              selectedBranch={selectedBranch}
              cardTitle="Branch Metrics"
              cardDescription="General Branch Metrics"
              tableViewOption={true}
              loading={loading}
            />
          </div> */}
        </div>
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  return <DashboardContent />
}
